import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { InputGroup, Button, Switch, Icon } from "@blueprintjs/core";
import { validate } from "validate.js";
import { Translate, I18n } from "react-redux-i18n";
import ReactPasswordStrength from "react-password-strength";
import validators from "../containers/auth/Register/grannbox_validators";
import ReactGA from "react-ga";
import { register } from "../store/iam/operations";
import { requestingAuth } from "../store/iam/selectors";
import { validatePhoneNumber } from "../utils/utils";
import LocationInput from "../components/GoogleLocation/LocationInput";
import PhoneNumberInput from "../components/PhoneInput/PhoneNumberInput";
import { AccountCircleRounded, EmailRounded, LockRounded } from "@material-ui/icons/";

let inputValues;

function isString(x) {
  return Object.prototype.toString.call(x) === "[object String]";
}

export default function GrannboxRegister({ goTo }) {
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({});
  const isLoading = useSelector(requestingAuth);
  const language = useSelector((state) => state.i18n.locale);
  const dispatch = useDispatch();
  inputValues = values;

  const updateFields = (event) => {
    let formValues = {};
    if ("password" in event) {
      formValues = {
        ...values,
        password: event.password,
      };
    } else {
      formValues = {
        ...inputValues,
        [event.target.id]: isString(event.target.value) ? event.target.value.trim() : event.target.value,
      };
    }

    setValues(formValues);
  };

  const termsAndConditionsChanged = (event) => {
    const formValues = {
      ...values,
      accepted_terms: event.target.checked,
    };
    setValues(formValues);
  };

  const submitForm = (e) => {
    e.preventDefault();
    let validationErrors = validate(values, validators);
    const mobile_phone_error = validatePhoneNumber(values.mobile_phone, true);
    if (mobile_phone_error) {
      if (!validationErrors) {
        validationErrors = {};
      }
      validationErrors.mobile_phone = [mobile_phone_error];
    }
    setErrors(validationErrors ? validationErrors : {});
    if (!validationErrors) {
      const payload = {
        locale: language,
        u_name: values.user_name,
        email: values.user_name,
        f_name: values.first_name,
        l_name: values.last_name,
        pw: values.password,
        address: values.address.address,
        country: values.address.country,
        city: values.address.city,
        lat: String(values.address.lat),
        lon: String(values.address.lon),
        mobile_phone: values.mobile_phone,
      };
      handleSubmit(payload);
    }
  };

  const handleSubmit = (payload) => {
    ReactGA.event({
      category: "User",
      action: "Created an Account",
    });
    dispatch(register(payload, null));
  };

  return (
    <div className="fade-on-entry">
      <div className="fake-secondary-cta" onClick={() => goTo("login")}>
        <Translate value="grannbox_onboarding.already_have_account" />
        <Icon icon="arrow-right" className="icon" />
      </div>
      <h1>
        <Translate value="grannbox_onboarding.create_account_header" />
      </h1>
      <p className="large-paragraph">
        <Translate value="grannbox_onboarding.create_account_subheader" />
      </p>
      <form onSubmit={submitForm}>
        <div className="split-name">
          <div className={errors.first_name ? "form-group error" : "form-group"}>
            {errors.first_name && (
              <label htmlFor="first_name">
                <Translate value={errors.first_name[0]} />
              </label>
            )}
            <InputGroup
              type="text"
              placeholder={I18n.t("sign_up.placeholders.first_name")}
              fill={true}
              id="first_name"
              disabled={isLoading}
              intent={errors.first_name ? "danger" : ""}
              onChange={updateFields}
              required
              rightElement={
                <div className="purchase-form-input-icon">
                  <AccountCircleRounded className="icon" size="16" />
                </div>
              }
            />
          </div>
          <div className={errors.last_name ? "form-group error" : "form-group"}>
            {errors.last_name && (
              <label htmlFor="last_name">
                <Translate value={errors.last_name[0]} />
              </label>
            )}
            <InputGroup
              type="text"
              placeholder={I18n.t("sign_up.placeholders.last_name")}
              fill={true}
              id="last_name"
              disabled={isLoading}
              intent={errors.last_name ? "danger" : ""}
              onChange={updateFields}
              required
              rightElement={
                <div className="purchase-form-input-icon">
                  <AccountCircleRounded className="icon" size="16" />
                </div>
              }
            />
          </div>
        </div>

        <div className={errors.user_name ? "form-group error" : "form-group"}>
          {errors.user_name && (
            <label htmlFor="user_name">
              <Translate value={errors.user_name[0]} />
            </label>
          )}
          <InputGroup
            type="email"
            placeholder={I18n.t("sign_up.placeholders.email_address")}
            fill={true}
            id="user_name"
            disabled={isLoading}
            intent={errors.user_name ? "danger" : ""}
            onChange={updateFields}
            required
            rightElement={
              <div className="purchase-form-input-icon">
                <EmailRounded className="icon" size="16" />
              </div>
            }
          />
        </div>
        <PhoneNumberInput errors={errors.mobile_phone ? errors.mobile_phone[0] : null} updateFields={updateFields} />
        <LocationInput
          error={errors.address ? errors.address[0] : null}
          address={values.address ? values.address.address : ""}
          locationChanged={updateFields}
        />
        <div className="small-input-explainer address-explainer">
          <Translate value="grannbox_onboarding.why_address" />
        </div>
        <div className={errors.password ? "form-group error" : "form-group"}>
          {errors.password && (
            <label htmlFor="password">
              <Translate value={errors.password[0]} />
            </label>
          )}
          <div className="react-pass-strength">
            <ReactPasswordStrength
              minLength={5}
              minScore={2}
              required={true}
              scoreWords={[
                I18n.t("password_score.weak"),
                I18n.t("password_score.okay"),
                I18n.t("password_score.good"),
                I18n.t("password_score.strong"),
                I18n.t("password_score.stronger"),
              ]}
              tooShortWord={I18n.t("password_score.too_short")}
              changeCallback={updateFields}
              inputProps={{
                name: "password",
                autoComplete: "off",
                className: "bp3-input",
                placeholder: I18n.t("sign_up.placeholders.password"),
              }}
            />
            <div className="purchase-form-input-icon">
              <LockRounded className="icon" size="16" />
            </div>
          </div>
        </div>

        <div className={errors.accepted_terms ? "form-group error" : "form-group"}>
          {errors.accepted_terms && (
            <label htmlFor="accepted_terms">
              <Translate value={errors.accepted_terms[0]} />
            </label>
          )}
          <Switch checked={values.accepted_terms} onChange={termsAndConditionsChanged}>
            <span>
              <Translate value="sign_up.read_and_accept" />{" "}
              <a target="_blank" href="/privacy-policy">
                <Translate value="menu.privacy_policy" />
              </a>{" "}
              &amp;{" "}
              <a target="_blank" href="/terms-of-use">
                <Translate value="menu.terms_of_use" />
              </a>
            </span>
          </Switch>
        </div>
        <Button
          disabled={isLoading}
          loading={isLoading}
          className="register-button"
          intent="primary"
          fill={true}
          type="submit">
          <Translate value="sign_up.next_step" />
        </Button>
      </form>
      <div className="flexbox">
        <div className="fake-link" onClick={() => goTo("login")}>
          <Translate value="grannbox_onboarding.already_have_account" />
        </div>
      </div>
    </div>
  );
}
